import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { Input, Form, FormGroup, FormFeedback, Button, Card, CardBody, Spinner } from 'reactstrap';
import { userActions } from '../../actions';
import { selectRegistration } from '../../redux/modules/registerReducer';
import Turnstile from '../Turnstile/Turnstile';
import config from '../../config';
import '../AuthForm.css';

const RegisterForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [user, setUser] = useState({
    email: '',
    password: '',
    isOptedIn: true
  });
  const registrationState = useSelector(selectRegistration);

  // No sitekey configured (e.g. before the prod widget exists) means no widget
  // and no token requirement, so registration is unaffected.
  const siteKey = config.turnstileSiteKey;

  // Turnstile tokens are single-use. A failed registration consumed the token,
  // so reset the widget to issue a fresh one before the user retries.
  const wasRegistering = useRef(false);
  useEffect(() => {
    if (wasRegistering.current && !registrationState.registering) {
      setTurnstileReset((count) => count + 1);
    }
    wasRegistering.current = registrationState.registering;
  }, [registrationState.registering]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "isOptedIn") {
      setUser({
        ...user,
        isOptedIn: event.target.checked
      });
      return;
    }

    setUser({
      ...user,
      [name]: value
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitted(true);

    if (user.email && user.password && user.isOptedIn && (!siteKey || turnstileToken)) {
      dispatch(userActions.register(user, navigate, turnstileToken));
    }
  };

  const registering = registrationState.registering;
  const invalidEmail = submitted && !user.email;
  const invalidPassword = submitted && !user.password;
  const invalidOptIn = submitted && !user.isOptedIn;
  const invalidTurnstile = submitted && !!siteKey && !turnstileToken;

  return (
    <div className="auth-wrapper">
      <div className="auth-card">
        <Card>
          <CardBody>
            <div className="auth-header">
              <h2>Create Your Account</h2>
              <p>Start protecting your online identity today</p>
            </div>

            <Form onSubmit={handleSubmit} className="auth-form">
              <FormGroup>
                <label className="form-label" htmlFor="email">Email Address</label>
                <Input
                  invalid={invalidEmail}
                  type="email"
                  name="email"
                  id="email"
                  placeholder="you@example.com"
                  onChange={handleChange}
                />
                <FormFeedback>Email address is required</FormFeedback>
              </FormGroup>

              <FormGroup>
                <label className="form-label" htmlFor="password">Password</label>
                <Input
                  invalid={invalidPassword}
                  type="password"
                  name="password"
                  id="password"
                  placeholder="Create a strong password"
                  onChange={handleChange}
                />
                <FormFeedback>Password is required</FormFeedback>
              </FormGroup>

              <div className="auth-checkbox">
                <Input
                  defaultChecked={true}
                  invalid={invalidOptIn}
                  type="checkbox"
                  name="isOptedIn"
                  id="isOptedIn"
                  onChange={handleChange}
                />
                <label htmlFor="isOptedIn">
                  I agree to the <Link to="/privacy-policy">Privacy Policy</Link> and <Link to="/terms-of-service">Terms of Service</Link>
                </label>
              </div>
              {invalidOptIn && (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  Please accept the terms to continue
                </div>
              )}

              <div className="auth-turnstile">
                <Turnstile
                  siteKey={siteKey}
                  onToken={setTurnstileToken}
                  resetSignal={turnstileReset}
                />
                {invalidTurnstile && (
                  <div className="invalid-feedback" style={{ display: 'block' }}>
                    Please complete the verification check
                  </div>
                )}
              </div>

              <Button color="primary" disabled={registering} style={{ marginTop: '1.5rem' }}>
                {registering ? 'Creating Account...' : 'Create Free Account'}
              </Button>

              {registering && (
                <div className="auth-spinner">
                  <Spinner type="grow" />
                </div>
              )}

              <div className="auth-footer">
                <p>Already have an account? <Link to="/login">Sign in</Link></p>
              </div>
            </Form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default RegisterForm;
